import api from './api';

export interface OLT {
  id: string;
  spop_id: string;
  type: 'HIOSO' | 'HSGQ';
  ip_address: string;
  port: number;
  username: string;
  password: string;
  status: 'aktif' | 'nonaktif';
  created_at: string;
  updated_at: string;
  spop?: {
    id: string;
    name: string;
    areaCode: string;
  };
}

export interface PONData {
  ponId: string;
  information: string;
  onuTotal: number;
  onuOnline: number;
  onuOffline: number;
  status: 'healthy' | 'warning' | 'critical' | 'inactive' | 'error';
  lastUpdated: string;
}

export interface PONStatistics {
  totalPorts: number;
  activePorts: number;
  totalOnus: number;
  totalOnline: number;
  totalOffline: number;
  healthyPorts: number;
  warningPorts: number;
  criticalPorts: number;
  inactivePorts: number;
}

export interface OLTPonResponse {
  olt: OLT;
  ponData: PONData[];
  statistics: PONStatistics;
  retrievedAt: string;
}

export interface OLTWithPonData {
  olt: OLT;
  statistics: PONStatistics | null;
  connectionStatus: 'connected' | 'disconnected';
  error?: string;
  lastChecked: string;
}

export interface ConnectionTestResult {
  olt_id: string;
  ip_address: string;
  port: number;
  connected: boolean;
  response_time?: number;
  tested_at: string;
}

class OLTService {
  // Get all OLTs
  static async getAllOLTs(params?: {
    page?: number;
    limit?: number;
    search?: string;
    status?: string;
    type?: string;
    spop_id?: string;
  }) {
    const response = await api.get('/olts', { params });
    return response.data;
  }

  // Get OLT by ID
  static async getOLTById(id: string) {
    const response = await api.get(`/olts/${id}`);
    return response.data;
  }

  // Create new OLT
  static async createOLT(data: Omit<OLT, 'id' | 'created_at' | 'updated_at'>) {
    const response = await api.post('/olts', data);
    return response.data;
  }

  // Update OLT
  static async updateOLT(id: string, data: Partial<OLT>) {
    const response = await api.put(`/olts/${id}`, data);
    return response.data;
  }

  // Delete OLT
  static async deleteOLT(id: string) {
    const response = await api.delete(`/olts/${id}`);
    return response.data;
  }

  // Test OLT connection
  static async testConnection(id: string): Promise<{ success: boolean; message: string; data: ConnectionTestResult }> {
    const response = await api.post(`/olts/${id}/test`);
    return response.data;
  }

  // Get PON data from OLT device
  static async getPonData(id: string): Promise<{ success: boolean; message: string; data: OLTPonResponse }> {
    const response = await api.get(`/olts/${id}/pon-data`);
    return response.data;
  }

  // Get PON statistics only
  static async getPonStatistics(id: string): Promise<{ success: boolean; message: string; data: { olt_id: string; statistics: PONStatistics; retrievedAt: string } }> {
    const response = await api.get(`/olts/${id}/pon-statistics`);
    return response.data;
  }

  // Get all OLTs with PON data
  static async getAllOLTsWithPonData(params?: {
    spop_id?: string;
    include_offline?: boolean;
  }): Promise<{ success: boolean; message: string; data: OLTWithPonData[]; retrievedAt: string }> {
    const response = await api.get('/olts/pon-data/all', { params });
    return response.data;
  }

  // Get OLTs by SPOP
  static async getOLTsBySPOP(spopId: string) {
    const response = await api.get(`/olts/spop/${spopId}`);
    return response.data;
  }

  // Get network health statistics
  static async getNetworkHealth(params?: { spop_id?: string }): Promise<{ 
    success: boolean; 
    message: string; 
    data: {
      totalPorts: number;
      healthyPorts: number;
      warningPorts: number;
      criticalPorts: number;
      inactivePorts: number;
      totalOnline: number;
      totalOffline: number;
      retrievedAt: string;
      meta?: {
        totalOLTs: number;
        successfulConnections: number;
        failedConnections: number;
      };
    } 
  }> {
    const response = await api.get('/olts/network/health', { params });
    return response.data;
  }
}

export default OLTService;
